/**
 * AppVersionWatcher — polls /api/version every 5 minutes and shows a
 * HARD-refresh banner when the deployed backend SHA actually changes.
 *
 * Iter 167 (2026-06-04, Josh — Danny report fix):
 *   Reps reported the "A new version is available" banner firing every minute
 *   and auto-refreshing them mid-call. Two root causes:
 *     1) Backend `/api/version` fell back to `boot-<epoch>` when no git was
 *        available, so every backend restart looked like a deploy.
 *     2) Frontend composite key included `loaded_at` (backend boot timestamp),
 *        which also changed on every restart.
 *   Fix: backend returns a stable `"unknown"` when no real version exists,
 *   AND the frontend (a) compares ONLY `sha`, (b) ignores `unknown` / `dev`
 *   / empty values entirely, (c) polls every 5 minutes.
 *
 * Iter 175.3 (2026-02-13, Josh — "hard refresh on redeploy"):
 *   Soft `window.location.reload()` was sometimes serving the cached
 *   bundle, so a few reps stayed on the old build for hours after a
 *   deploy. We now:
 *     1) Trigger a HARD refresh — calls Cache API `delete` on every
 *        cache the SW may have populated, busts a query-string on
 *        `location.href`, and only THEN reloads. Forces fresh bundle.
 *     2) Auto-refresh after a 30s countdown when the rep is NOT in an
 *        active call (WebPhone dock collapsed = safe). Reps mid-call
 *        get the banner but no auto-clock; they choose when to click.
 *     3) Reps can dismiss the auto-countdown if they're about to dial
 *        ("Dismiss" cancels the auto-refresh but the banner stays).
 *
 * Design notes:
 *   • Boot SHA is captured on first successful poll, NOT on app mount, so
 *     transient network errors don't trip a false-positive refresh.
 *   • Auto-refresh ONLY fires when the WebPhone dock is collapsed
 *     (data-expanded="false" or absent) — protects reps mid-call.
 *   • The countdown pauses + resets if the dock opens during it.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const POLL_MS = 60_000; // 60 seconds — Iter 178.4 (Josh Feb 2026):
// Previously 5 minutes, which left reps on the stale bundle for up to
// 5 min after a redeploy. Josh wants ~instant pickup — 60 s caps the
// worst-case detection window at 1 minute. We also poll on `focus`
// (see below) so the common "alt-tab back into the CRM" case picks up
// the new SHA the instant the rep returns to the page.
// `/api/version` is a cheap SHA lookup with no DB; we can afford this.
// Iter 175.3 — once a new SHA is detected, count down 30s and then
// auto-refresh ONLY if the rep isn't on an active call. Keeps the
// "everyone runs the latest build" invariant Josh asked for without
// kicking reps out mid-conversation.
const AUTO_REFRESH_COUNTDOWN_S = 30;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// SHAs that mean "we don't know what version this is". Never trigger the banner
// for these — the backend is reporting a fallback sentinel because git wasn't
// available, so we cannot meaningfully compare versions.
const IGNORED_SHAS = new Set(["unknown", "dev", "", null, undefined]);

const isMeaningfulSha = (sha) => {
  if (!sha) return false;
  if (IGNORED_SHAS.has(sha)) return false;
  // Reject legacy boot-<epoch> values that an older backend might still emit
  // until it's redeployed. They change on every restart and would re-trigger
  // the bug we're trying to fix.
  if (/^boot-\d+$/.test(sha)) return false;
  return true;
};

export default function AppVersionWatcher() {
  const [bootSha, setBootSha] = useState(null);
  const [newSha, setNewSha] = useState(null);
  // Iter 173 (Danny — daily 7 AM ET forced re-login):
  // Track the next forced-reauth window and show a 10-min warning
  // banner. When the countdown hits 0 OR a request 401s with
  // `forced_reauth`, hard-redirect to /login (the reload also pulls
  // the freshest bundle, killing the stale-code problem for good).
  const [reauthWarning, setReauthWarning] = useState(false);
  // Iter 175.3 — auto-refresh countdown state. Null = no countdown
  // active (rep is on a call or already dismissed).
  const [autoSecs, setAutoSecs] = useState(null);
  const [autoCancelled, setAutoCancelled] = useState(false);
  const intervalRef = useRef(null);
  const reauthIntervalRef = useRef(null);
  const autoIntervalRef = useRef(null);

  // Iter 175.3 — true HARD refresh. Soft `location.reload()` was
  // sometimes serving the cached bundle; this one nukes the SW cache
  // (if any), busts a `?_v=<sha>` query-string on the URL so the
  // browser MUST re-request the index.html, and then reloads.
  const hardRefresh = async () => {
    try {
      if (window.caches && typeof window.caches.keys === "function") {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      }
    } catch { /* private mode / Safari */ }
    try {
      // Drop tokens that might gate the new bundle's auth flow on
      // edge cases (we keep auth — only sessionStorage is wiped).
      sessionStorage.clear();
    } catch { /* noop */ }
    // Cache-bust by appending a fresh `_v=` query param to the URL so
    // the browser cannot serve the cached document.
    const url = new URL(window.location.href);
    url.searchParams.set("_v", String(Date.now()));
    window.location.replace(url.toString());
  };

  // Iter 175.3 — auto-refresh countdown effect. Fires only when a new
  // SHA is detected AND the rep hasn't cancelled the countdown. Pauses
  // whenever the global WebPhone dock is expanded (rep is mid-call).
  useEffect(() => {
    if (!newSha || autoCancelled) {
      // No-op when there's nothing to count down. The interval below
      // never started in this branch, so no cleanup is needed.
      return undefined;
    }
    const isOnCall = () => {
      const dock = document.querySelector('[data-testid="global-webphone-dock"]');
      return dock?.getAttribute("data-expanded") === "true";
    };
    // Seed the countdown once when the effect starts. Subsequent
    // updates happen inside setInterval, never in the effect body.
    let cleared = false;
    const seed = setTimeout(() => {
      if (cleared) return;
      setAutoSecs(AUTO_REFRESH_COUNTDOWN_S);
    }, 0);
    autoIntervalRef.current = setInterval(() => {
      setAutoSecs((s) => {
        if (s === null) return s;
        if (isOnCall()) return AUTO_REFRESH_COUNTDOWN_S;
        if (s <= 1) {
          hardRefresh();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      cleared = true;
      clearTimeout(seed);
      if (autoIntervalRef.current) clearInterval(autoIntervalRef.current);
      // Reset state on unmount so re-mount starts clean.
      setAutoSecs(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSha, autoCancelled]);

  // Boot + polling loop
  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/version`, { timeout: 8000 });
        const sha = r?.data?.sha;
        if (!mounted) return;
        // Iter 178.10 (Josh Feb 2026) — record ANY sha as bootSha on
        // the very first poll, including the legacy "unknown" sentinel.
        // Previously we filtered with isMeaningfulSha() BEFORE storing,
        // which meant reps whose session started on a "unknown"-returning
        // build (production pre-178.10) had bootSha=null forever — so
        // when the next deploy started returning a real SHA, the watcher
        // treated it as a first boot, not a deploy transition. Banner
        // never fired. Fix: store the literal first response as the
        // baseline; only require MEANINGFUL on the COMPARISON side so
        // we don't trigger the banner on dev/unknown ↔ dev/unknown
        // flips.
        if (!bootSha) {
          setBootSha(sha || "_uninit_");
          return;
        }
        // Banner trigger conditions:
        //   1. The fresh SHA is meaningful (not unknown/dev/empty)
        //   2. It differs from the baseline AND from any pending newSha
        if (!isMeaningfulSha(sha)) return;
        if (sha !== bootSha && sha !== newSha) {
          setNewSha(sha);
        }
      } catch {
        // network errors are non-fatal; do nothing.
      }
    };
    poll();
    intervalRef.current = setInterval(poll, POLL_MS);
    // Iter 178.4 (Josh Feb 2026) — also poll the instant the rep
    // returns to the tab. Most "I redeployed and no banner appeared"
    // cases are reps who alt-tab away for a few seconds and come back:
    // the next setInterval tick might be 50s out, so we trigger an
    // immediate poll on focus to close that gap. Throttled by the
    // built-in `!mounted` guard inside `poll()`.
    const onFocus = () => { poll(); };
    window.addEventListener("focus", onFocus);
    return () => {
      mounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootSha]);

  // Iter 173 — forced-reauth countdown poll (every minute).
  useEffect(() => {
    let mounted = true;
    const _doLogout = (reason) => {
      try {
        localStorage.removeItem("token");
        sessionStorage.clear();
      } catch { /* noop */ }
      // Hard reload to /login — fresh bundle + clean session.
      window.location.assign(`/login?from=${encodeURIComponent(reason || "forced_reauth")}`);
    };
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/forced-reauth/next`, { timeout: 6000 });
        if (!mounted) return;
        const secs = Number(r?.data?.seconds_until || 0);
        if (secs <= 0) {
          _doLogout("scheduled_refresh");
          return;
        }
        if (secs <= 600) setReauthWarning(true);
        else setReauthWarning(false);
      } catch { /* network errors non-fatal */ }
    };
    tick();
    reauthIntervalRef.current = setInterval(tick, 60_000);
    // Also listen for any 401 from anywhere via a global axios
    // interceptor — single source of truth for the forced_reauth case.
    const intId = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 401 && detail === "forced_reauth") {
          _doLogout("forced_reauth_401");
        }
        return Promise.reject(err);
      },
    );
    return () => {
      mounted = false;
      if (reauthIntervalRef.current) clearInterval(reauthIntervalRef.current);
      try { axios.interceptors.response.eject(intId); } catch { /* noop */ }
    };
  }, []);

  // Render: only show one banner at a time. Forced-reauth warning
  // takes priority because it's time-critical.
  if (reauthWarning) {
    return (
      <div
        data-testid="forced-reauth-warning"
        role="status"
        aria-live="polite"
        style={{
          position: "fixed", right: 16, bottom: 16, zIndex: 9999,
          maxWidth: 380, padding: "12px 14px",
          background: "#854F0B", color: "#FFF7E6",
          borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.28)",
          display: "flex", gap: 12, alignItems: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontSize: 14, lineHeight: 1.35,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Daily refresh at 7:00 AM ET</div>
          <div style={{ opacity: 0.9, marginTop: 2, fontSize: 12 }}>
            You&apos;ll be signed out in under 10 min — finish your current call,
            then click Refresh. Saved work is safe.
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            try { localStorage.removeItem("token"); sessionStorage.clear(); } catch { /* noop */ }
            window.location.assign("/login?from=manual_refresh");
          }}
          data-testid="forced-reauth-refresh-now"
          style={{
            background: "#FFF7E6", color: "#854F0B",
            border: "none", padding: "7px 12px",
            borderRadius: 7, fontWeight: 600,
            cursor: "pointer", fontSize: 13,
          }}
        >
          Refresh now
        </button>
      </div>
    );
  }

  if (!newSha) return null;

  const onRefresh = () => hardRefresh();
  const onDismiss = () => setNewSha(null);
  const onCancelAuto = () => setAutoCancelled(true);

  return (
    <div
      data-testid="app-version-banner"
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 9999,
        maxWidth: 380,
        padding: "12px 14px",
        background: "#0f172a",
        color: "#fff",
        borderRadius: 10,
        boxShadow: "0 8px 28px rgba(0,0,0,0.28)",
        display: "flex",
        gap: 12,
        alignItems: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 14,
        lineHeight: 1.35,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>A new version is available</div>
        <div style={{ opacity: 0.8, marginTop: 2, fontSize: 12 }}>
          {autoSecs !== null && autoSecs > 0
            ? `Auto-refreshing in ${autoSecs}s (paused if you're on a call) — click Dismiss to refresh manually.`
            : "Refresh when you're ready — your work is saved."}
        </div>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        data-testid="app-version-refresh-btn"
        style={{
          background: "#22c55e",
          color: "#0b1220",
          border: "none",
          padding: "7px 12px",
          borderRadius: 7,
          fontWeight: 600,
          cursor: "pointer",
          fontSize: 13,
        }}
      >
        Refresh now
      </button>
      <button
        type="button"
        onClick={autoSecs !== null && autoSecs > 0 ? onCancelAuto : onDismiss}
        aria-label={autoSecs !== null && autoSecs > 0 ? "Cancel auto-refresh" : "Dismiss"}
        data-testid="app-version-dismiss-btn"
        title={autoSecs !== null && autoSecs > 0 ? "Cancel auto-refresh" : "Dismiss"}
        style={{
          background: "transparent",
          color: "#cbd5e1",
          border: "none",
          cursor: "pointer",
          fontSize: 18,
          lineHeight: 1,
          padding: "0 2px",
        }}
      >
        ×
      </button>
    </div>
  );
}
